// DLC_02 · Zlecenia wydruku (print orders) API. The viewset only parses multipart/form-data,
// so all writes post FormData (empty FormData for the no-body actions).
import apiClient from "@/api/client";
import type { PrintOrder } from "./types";

const BASE = "/print-orders";
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } } as const;

async function unwrap<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  const r = await p;
  return r.data.data;
}

export const getPrintOrders = (params?: Record<string, string>) =>
  unwrap<PrintOrder[]>(apiClient.get(`${BASE}/`, { params: { page_size: 1000, ...params } }));

export const getPrintOrdersPendingCount = () =>
  unwrap<number>(apiClient.get(`${BASE}/pending-count/`));

// Create a print order (FormData: name, description, needs_shipping + the single `file`).
export const createPrintOrder = (fd: FormData) =>
  unwrap<PrintOrder>(apiClient.post(`${BASE}/`, fd, MULTIPART));

// Edit a pending print order (FormData: name, description, needs_shipping + optional new `file`).
export const updatePrintOrder = (id: string, fd: FormData) =>
  unwrap<PrintOrder>(apiClient.patch(`${BASE}/${id}/`, fd, MULTIPART));

// Delete a print order that has not been sent yet.
export const deletePrintOrder = (id: string) =>
  unwrap<null>(apiClient.delete(`${BASE}/${id}/`));

// Upload an inline image for the rich-text description; returns its URL for embedding.
export const uploadPrintOrderDescriptionImage = (file: File): Promise<string> => {
  const fd = new FormData();
  fd.append("file", file);
  return unwrap<{ url: string }>(
    apiClient.post(`${BASE}/upload-description-image/`, fd, MULTIPART),
  ).then((d) => d.url);
};

const postAction = (id: string, action: string, fd: FormData = new FormData()) =>
  unwrap<PrintOrder>(apiClient.post(`${BASE}/${id}/${action}/`, fd, MULTIPART));

export const acceptPrintOrder = (id: string) => postAction(id, "accept");
export const rejectPrintOrder = (id: string, note: string) => {
  const fd = new FormData();
  if (note) fd.append("note", note);
  return postAction(id, "reject", fd);
};
export const cancelPrintOrder = (id: string) => postAction(id, "cancel");
export const markPrintOrderPrinted = (id: string) => postAction(id, "mark-printed");
export const markPrintOrderSent = (id: string) => postAction(id, "mark-sent");
// Undo an accidental "Wydrukowane" (printed → accepted): author, technical director or CEO.
export const undoPrintOrderPrinted = (id: string) => postAction(id, "undo-printed");
// Flip „Potrzebna wysyłka" on an ACCEPTED order (technical director / CEO). Regular editing
// closes on acceptance; this is the one field that may still need correcting before printing.
export const setPrintOrderShipping = (id: string, needsShipping: boolean) => {
  const fd = new FormData();
  fd.append("needs_shipping", needsShipping ? "true" : "false");
  return postAction(id, "set-shipping", fd);
};

// Kilka zwrotek pod jednym zleceniem: several PDFs may be posted at once and the backend merges
// them into the ONE file the order carries. Without `mode: "replace"` the pages are appended to
// what is already attached (druga zwrotka potrafi wrócić kilka dni później).
const pdfForm = (files: File[], mode?: "append" | "replace") => {
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f));
  if (mode === "replace") fd.append("mode", "replace");
  return fd;
};

// Attach (append to, or replace) the proof-of-delivery PDF on a shipped order (status=sent).
export const uploadPrintOrderReceiptConfirmation = (
  id: string,
  files: File[],
  mode?: "append" | "replace",
) => postAction(id, "receipt-confirmation", pdfForm(files, mode));

// Nr listu dopisany do wysłanego zlecenia (także po zwrocie) — potwierdzenie nadania z poczty
// rzadko leży na biurku w chwili klikania „Wysłane". Ten sam krąg co oznaczanie wysyłki.
export const setPrintOrderTrackingNumber = (id: string, trackingNumber: string) => {
  const fd = new FormData();
  fd.append("tracking_number", trackingNumber);
  return postAction(id, "tracking-number", fd);
};

// Mark a shipped order as returned (sent → returned), optionally attaching the return scans (PDF).
export const markPrintOrderReturned = (id: string, files?: File[] | null) =>
  postAction(id, "mark-returned", pdfForm(files ?? []));

// Attach (append to, or replace) the return-scan PDF on an order already marked as returned.
export const uploadPrintOrderReturnAttachment = (
  id: string,
  files: File[],
  mode?: "append" | "replace",
) => postAction(id, "return-attachment", pdfForm(files, mode));
